import { createRouter, createWebHistory, RouteLocationNormalized, RouteRecordRaw } from 'vue-router';

function isDesktopRuntime() {
  if (typeof window === 'undefined') {
    return false;
  }

  return Boolean(window.__CAFE_DESKTOP_RUNTIME__);
}

function isAllowedLocalAppOrigin() {
  if (typeof window === 'undefined') {
    return false;
  }

  const hostname = window.location.hostname || '';

  return (
    hostname === 'localhost'
    || hostname === '127.0.0.1'
    || hostname === '0.0.0.0'
    || hostname === '::1'
    || /^10\./.test(hostname)
    || /^192\.168\./.test(hostname)
    || /^172\.(1[6-9]|2\d|3[0-1])\./.test(hostname)
  );
}

function hasDesktopSession() {
  return Boolean(getDesktopSessionUser()?.id);
}

function getDesktopSessionUser() {
  if (typeof window === 'undefined') {
    return null;
  }

  try {
    const raw = window.localStorage.getItem('cafe-desktop-session');
    if (!raw) {
      return null;
    }

    const parsed = JSON.parse(raw) as { user?: { id?: string; role?: string } | null } | null;
    return parsed?.user?.id ? parsed.user : null;
  } catch (error) {
    console.warn('Failed to inspect desktop session state', error);
    return null;
  }
}

function hasDesktopRole(roles: string[]) {
  const user = getDesktopSessionUser();
  return Boolean(user?.role && roles.includes(user.role));
}

function requireLocalSurface(roles?: string[]) {
  if (!isDesktopRuntime() && !isAllowedLocalAppOrigin()) {
    return '/';
  }

  if (isDesktopRuntime() && !hasDesktopSession()) {
    return '/desktop-entry';
  }

  if (isDesktopRuntime() && roles?.length && !hasDesktopRole(roles)) {
    return '/desktop-entry';
  }

  return true;
}

// v2 screens always need a signed-in person (browser or desktop): without one,
// or with the wrong role, they go to the v2 start screen and come back after.
function requireV2(roles: string[], to: RouteLocationNormalized) {
  if (!isDesktopRuntime() && !isAllowedLocalAppOrigin()) {
    return '/';
  }
  const user = getDesktopSessionUser();
  if (!user?.role || !roles.includes(user.role)) {
    return { path: '/v2/start', query: { next: to.fullPath } };
  }
  return true;
}

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'portal',
    component: () => import('../components/DesktopPortalShell.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  // ── Switched to the v2 interface. The previous screens stay reachable under
  // /old/… until they are removed; the old addresses (and printed QR cards)
  // lead to the matching new screens.
  {
    path: '/desktop-entry',
    alias: '/login',
    redirect: (to) => ({ path: '/v2/start', query: to.query }),
  },
  {
    path: '/admin',
    redirect: '/v2',
  },
  {
    path: '/kitchen',
    redirect: '/v2/kitchen',
  },
  {
    path: '/table',
    redirect: '/v2/waiter',
  },
  {
    path: '/table/:tableId',
    redirect: (to) => ({ path: `/v2/t/${String(to.params.tableId || '').toLowerCase()}`, query: {} }),
  },
  {
    path: '/old/desktop-entry',
    component: () => import('../views/DesktopWelcomeView.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/old/table',
    beforeEnter: () => requireLocalSurface(['admin', 'waiter']),
    component: () => import('../../apps/table/TablePage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/old/table/:tableId',
    beforeEnter: () => requireLocalSurface(),
    component: () => import('../../apps/table/TablePage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/storefront',
    redirect: '/v2/display',
  },
  {
    path: '/v2/display',
    beforeEnter: () => requireLocalSurface(),
    component: () => import('../v2/pages/DisplayPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/old/storefront',
    beforeEnter: () => requireLocalSurface(),
    component: () => import('../../apps/desktop/StorefrontDisplayPage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/admin/statistics',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/admin/AdminSalesStatisticsPage.vue'),
  },
  {
    path: '/old/admin',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/admin/AdminPage.vue'),
  },
  {
    // Подписка «Cafe Local Pro»: статус, оплата через Stripe, кабинет и история.
    path: '/admin/subscription',
    alias: '/extensions',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/admin/SubscriptionPage.vue'),
  },
  {
    path: '/portal-owner',
    beforeEnter: () => requireLocalSurface(['admin']),
    component: () => import('../../apps/portal-owner/PortalOwnerAdminPage.vue'),
    meta: {
      hideTopNav: true,
    },
  },
  {
    path: '/old/kitchen',
    beforeEnter: () => requireLocalSurface(['admin', 'kitchen']),
    component: () => import('../../apps/kitchen/KitchenPage.vue'),
  },
  // ── v2 interface (design-v2): a copy of the frontend on its own addresses.
  // The screens above stay as they are.
  {
    // Guest menu opened from the table QR code.
    path: '/v2/t/:code',
    beforeEnter: () => requireLocalSurface(),
    component: () => import('../v2/pages/GuestPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    // First run: the page itself decides who may use it (see SetupPage).
    path: '/v2/setup',
    beforeEnter: () => (!isDesktopRuntime() && !isAllowedLocalAppOrigin() ? '/' : true),
    component: () => import('../v2/SetupPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/start',
    beforeEnter: () => (!isDesktopRuntime() && !isAllowedLocalAppOrigin() ? '/' : true),
    component: () => import('../v2/StartPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/TodayPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/menu',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/MenuPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/profit',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/ProfitPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/products',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/ProductsPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    // Opened on a phone by scanning the QR on a sign-in screen.
    path: '/v2/approve/:id',
    beforeEnter: (to) => requireV2(['admin', 'staff', 'kitchen', 'waiter'], to),
    component: () => import('../v2/ApprovePage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/ideas',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/IdeasPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/free',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/FreePage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/owner',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/OwnerPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/ai/report',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/AiReportPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/ai',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/AiPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/sales',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/SalesPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/purchases',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/PurchasesPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/tables',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/TablesPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/settings',
    beforeEnter: (to) => requireV2(['admin'], to),
    component: () => import('../v2/pages/SettingsPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/kitchen',
    beforeEnter: (to) => requireV2(['admin', 'kitchen'], to),
    component: () => import('../v2/pages/KitchenPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/v2/waiter',
    beforeEnter: (to) => requireV2(['admin', 'waiter', 'staff'], to),
    component: () => import('../v2/pages/WaiterPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/cafe-local/terms',
    component: () => import('../v2/pages/CafeLegalPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/cafe-local/privacy',
    component: () => import('../v2/pages/CafeLegalPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/cafe-local/cookies',
    component: () => import('../v2/pages/CafeLegalPage.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/privacy',
    component: () => import('../views/PrivacyPolicyView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/terms',
    component: () => import('../views/TermsView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/cookies',
    component: () => import('../views/CookiePolicyView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/geolocation',
    component: () => import('../views/GeolocationConsentView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/venue-rules',
    component: () => import('../views/VenueRulesView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/account-deletion',
    component: () => import('../views/AccountDeletionView.vue'),
    meta: { hideTopNav: true },
  },
  {
    path: '/:catchAll(.*)',
    redirect: '/',
  },
];

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, from, savedPosition) {
    if (to.path === '/admin' || to.path === '/admin/statistics') {
      return { top: 0, left: 0 };
    }

    if (savedPosition) {
      return savedPosition;
    }

    return { top: 0, left: 0 };
  },
});

// After the app is updated, an open page still knows the old script file
// names; opening another screen then fails to load it. Reload once into the
// screen the person wanted, which picks up the new version.
const RELOAD_KEY = 'cafe-chunk-reload';
router.onError((error, to) => {
  const text = String((error && (error as Error).message) || error);
  if (!/Loading (CSS )?chunk|Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module/i.test(text)) return;
  try {
    const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 10000) return; // already tried: avoid a reload loop
    sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch { /* reload anyway */ }
  window.location.assign(to?.fullPath || window.location.href);
});

export default router;
